// Single source of truth for the portal sidebar.
// Sidebar component renders this recursively and lights up the active item
// based on the current Inertia URL.

export type NavItem = {
  label: string
  icon?: string
  href?: string
  /** Optional badge count rendered as a chip on the right. Live for
   *  Tickets queue children (sourced from the `ticketsNav` shared prop). */
  count?: number
  /** Capability key from config/capabilities.yml. When present, the
   *  Sidebar hides this item from any user who lacks the capability. A
   *  parent item with children is hidden only when ALL of its children
   *  are also hidden — so e.g. agents see "Settings → My Profile / My
   *  Security" without the rest of the Settings group. */
  requiresCapability?: string
  children?: NavItem[]
}

export const navigation: NavItem[] = [
  { label: "Dashboard", icon: "🏠", href: "/dashboard", requiresCapability: "dashboard_view" },
  // Tickets — children are rendered DYNAMICALLY by the Sidebar component
  // from the `ticketsNav` shared inertia prop (All / My / per-queue rows
  // with live counts). Don't add static children here; they get hidden
  // by the dynamic merge.
  { label: "Tickets", icon: "🎟", href: "/tickets" },
  {
    label: "Agents", icon: "👤", href: "/agents",
    requiresCapability: "agents_view",
    children: [
      { label: "All Agents", href: "/agents" },
      { label: "Topics",     href: "/topics" },
      { label: "Escalation Reasons", href: "/escalation_reasons" },
      { label: "Agent Pools", href: "/agent_pools", requiresCapability: "agent_pools_edit" },
      { label: "Escalation Policies", href: "/escalation_policies", requiresCapability: "escalation_policies_edit" },
      { label: "Skills", href: "/skills", requiresCapability: "skills_view" },
      { label: "Testing", href: "/testing", requiresCapability: "skills_edit" },
    ],
  },
  { label: "Company Endpoints", icon: "🔌", href: "/endpoints", requiresCapability: "endpoints_view" },
  {
    label: "User Feedback", icon: "💬", href: "/user_feedback",
    requiresCapability: "user_feedback_view",
    children: [
      { label: "All",            href: "/user_feedback" },
      { label: "Recommendations", href: "/user_feedback?category=recommendation" },
      { label: "Bugs",           href: "/user_feedback?category=bug" },
      { label: "Unresolved",     href: "/user_feedback?category=unresolved" },
    ],
  },
  {
    label: "Analytics", icon: "📊", href: "/analytics",
    requiresCapability: "analytics_view",
    children: [
      { label: "Overview",     href: "/analytics" },
      { label: "Performance",  href: "/analytics/handlers" },
      { label: "Escalations",  href: "/analytics/escalations" },
      { label: "SLA Performance", href: "/analytics/sla" },
    ],
  },
  {
    label: "Settings", icon: "⚙", href: "/settings?tab=profile",
    children: [
      { label: "Company",     href: "/settings?tab=company", requiresCapability: "settings_company_profile" },
      { label: "Team",        href: "/settings/team",        requiresCapability: "team_view" },
      { label: "Integration", href: "/settings?tab=integration", requiresCapability: "api_keys_view" },
      { label: "My Profile",  href: "/settings?tab=profile" },
      { label: "My Security", href: "/settings?tab=security" },
    ],
  },
]
